import { VlocPreQualified } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LendingCard from 'shared/components/vxl/cards/LendingCard/LendingCard';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveMdText from 'shared/components/vxl/typography/expressive/ExpressiveMdText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';

type PrequalApprovedScreenProps = {
  data?: VlocPreQualified | PrequalApprovedData;
};

type LendingTier = {
  id: string;
  tierAmount?: {
    amount?: string;
    formattedWithPlusPrefix?: string;
  } | null;
  term?: string | null;
  mothlyPayment?: string | null;
  fee?: {
    amount?: string;
    formattedWithPlusPrefix?: string;
  } | null;
};

type PrequalApprovedData = {
  headerTitle?: string | null;
  headerBody?: string | null;
  headerFooter?: string | null;
  registrationInfoId?: string | null;
  vlocTiers?: LendingTier[];
};

const PrequalApprovedScreen = ({ data }: PrequalApprovedScreenProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const { bottom } = useSafeAreaInsets();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/prequal-approved',
  });

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VLOC_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_TRACKER_CARD_TILE_CREDIT_TRACKER,
  ]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  useBackHandler(goHome);

  const vlocTierData = data?.vlocTiers ?? null;
  const registrationInfoId = data?.registrationInfoId ?? null;

  const formattedCardData = vlocTierData?.map((tier) => ({
    tierId: tier.id,
    text: String(tier.tierAmount?.formattedWithPlusPrefix ?? '').split('.')[0],
    details: [
      {
        id: 1,
        titleText: translatedText('triesData.term'),
        subtitleText: tier.term,
      },
      {
        id: 2,
        titleText: translatedText('triesData.monthlyPayment'),
        subtitleText: tier.mothlyPayment,
      },
      {
        id: 3,
        titleText: translatedText('triesData.fee'),
        subtitleText: String(tier.fee?.formattedWithPlusPrefix ?? '').split(
          '.',
        )[0],
      },
    ],
    analytics: {
      amount: tier?.tierAmount?.amount ?? '',
      source_name: 'vloc_tier_selection_page',
      term: tier?.term,
      fee: tier?.fee?.amount ?? '',
    },
  }));

  const handleCardPress = (registrationInfoId: string, tierId: string) => {
    router.push({
      pathname: '/varo-line-of-credit/apply-employment',
      params: {
        registrationInfoId: registrationInfoId,
        tierId: tierId,
      },
    });
  };

  return (
    <View style={[styles.mainContainer, { paddingBottom: bottom }]}>
      <ScrollView bounces={false} alwaysBounceVertical={false}>
        <Stack.Screen
          options={{
            headerBackVisible: true,
            headerShown: true,
            gestureEnabled: false,
            headerTransparent: false,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                primaryIcon={
                  <Icon iconKey="CLOSE" iconColor={theme.color.text.inverse} />
                }
                primaryOnPress={goHome}
                color={theme.color.expressive.varoPurple}
                {...props}
              />
            ),
          }}
        />
        <View style={styles.container}>
          {data?.headerTitle ? (
            <ExpressiveMdText style={styles.headerText} numberOfLines={0}>
              {data.headerTitle}
            </ExpressiveMdText>
          ) : null}
          {data?.headerBody ? (
            <BodyBaseText style={styles.contentText} numberOfLines={0}>
              {data.headerBody}
            </BodyBaseText>
          ) : null}
          {data?.headerFooter ? (
            <BodySmText style={styles.contentText} numberOfLines={0}>
              {data.headerFooter}
            </BodySmText>
          ) : null}
        </View>
        <View style={styles.bottomContainer}>
          {formattedCardData?.map((card, index) => (
            <View
              key={card.tierId}
              style={
                index !== formattedCardData?.length - 1 && styles.cardSpacing
              }
            >
              <LendingCard
                onPress={() =>
                  handleCardPress(registrationInfoId ?? '', card.tierId)
                }
                text={card.text}
                details={card.details}
                analyticsId="prequal_vloc_tier_selected"
                customAttributes={card?.analytics as Record<string, string>}
              />
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    padding: theme.spacing.m,
  },
  cardSpacing: {
    marginBottom: theme.spacing.m,
  },
  container: {
    alignSelf: 'center',
    backgroundColor: theme.color.expressive.varoPurple,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
    width: '100%',
  },
  contentText: {
    color: theme.color.text.inverse,
  },
  headerText: {
    color: theme.color.expressive.blush,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default PrequalApprovedScreen;
